import React, { useEffect, useMemo, useState } from "react";
import { Eye, Pencil, RotateCcw, Save, Trash2, Users, WalletCards } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

const defaultPartners = [
  { id: "partner_1", name: "S", percent: "30" },
  { id: "partner_2", name: "D", percent: "70" },
];

const emptyForm = {
  id: "",
  entry_date: getIndiaDateValue(),
  entry_type: "debit",
  title: "",
  category: "",
  amount: "",
  note: "",
  bank_id: "",
  to_bank_id: "",
};

const emptyPartnerPaymentForm = {
  payment_date: getIndiaDateValue(),
  payment_type: "taken_from_profit",
  amount: "",
  note: "",
  bank_id: "",
};

const emptyBankForm = {
  id: "",
  bank_name: "",
  account_label: "",
  opening_balance: "",
  is_default_deposit: false,
  is_default_withdraw: false,
  is_expense_default: false,
};

function AdminExpenses() {
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [data, setData] = useState({ settings: {}, summary: {}, entries: [], deleted_entries: [], banks: [] });
  const [form, setForm] = useState(emptyForm);
  const [bankForm, setBankForm] = useState(emptyBankForm);
  const [partners, setPartners] = useState(defaultPartners);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [agentToAdd, setAgentToAdd] = useState("");
  const [openPartner, setOpenPartner] = useState(null);
  const [partnerPaymentForm, setPartnerPaymentForm] = useState(emptyPartnerPaymentForm);
  const [tab, setTab] = useState("active");
  const [loading, setLoading] = useState(false);
  const isMobile = viewportWidth <= 780;

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const res = await API.get("/admin/expenses");
      const payload = res.data || {};
      setData({
        settings: payload.settings || {},
        summary: payload.summary || {},
        entries: Array.isArray(payload.entries) ? payload.entries : [],
        deleted_entries: Array.isArray(payload.deleted_entries) ? payload.deleted_entries : [],
        banks: Array.isArray(payload.banks) ? payload.banks : [],
        agents: Array.isArray(payload.agents) ? payload.agents : [],
        agent_commissions: Array.isArray(payload.agent_commissions) ? payload.agent_commissions : [],
        partner_payments: Array.isArray(payload.partner_payments) ? payload.partner_payments : [],
      });
      setPartners(normalizePartners(payload.settings?.partners));
      setSelectedAgents(normalizeAgentSettings(payload.settings?.agent_commission_agents || payload.settings?.agent_ids));
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to load expenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenses();
  }, []);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const rows = tab === "deleted" ? data.deleted_entries : data.entries;
  const banks = Array.isArray(data.banks) ? data.banks : [];
  const defaultExpenseBank = banks.find((bank) => Number(bank.is_expense_default || 0)) || banks[0] || null;
  const splitTotal = useMemo(() => partners.reduce((sum, partner) => sum + Number(partner.percent || 0), 0), [partners]);
  const availableAgents = Array.isArray(data.agents) ? data.agents : [];
  const selectedAgentSet = useMemo(() => new Set(selectedAgents.map((agent) => Number(agent.id))), [selectedAgents]);
  const selectedAgentStatus = useMemo(() => new Map(selectedAgents.map((agent) => [Number(agent.id), Boolean(agent.settled)])), [selectedAgents]);
  const addableAgents = availableAgents.filter((agent) => !selectedAgentSet.has(Number(agent.id)));
  const visibleAgentCommissions = (data.agent_commissions || [])
    .filter((agent) => selectedAgentSet.has(Number(agent.id)))
    .map((agent) => ({
      ...agent,
      settled: selectedAgentStatus.has(Number(agent.id)) ? selectedAgentStatus.get(Number(agent.id)) : Boolean(agent.settled),
    }));
  const partnerSettlementMap = useMemo(() => new Map((data.summary.partner_settlements || []).map((item) => [String(item.partner_id), item])), [data.summary.partner_settlements]);
  const splitBase = Number(data.summary.partnerSplitBase ?? data.summary.profitAfterAgentCommission ?? 0);
  const previewPartnerShares = useMemo(() => partners.map((partner) => ({
    ...partner,
    amount: splitBase * (Number(partner.percent || 0) / 100),
  })), [partners, splitBase]);

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const updatePartner = (id, key, value) => {
    setPartners((current) => current.map((partner) => partner.id === id ? { ...partner, [key]: value } : partner));
  };

  const addPartner = () => {
    setPartners((current) => [
      ...current,
      { id: `partner_${Date.now()}`, name: `Partner ${current.length + 1}`, percent: "0" },
    ]);
  };

  const removePartner = (id) => {
    setPartners((current) => current.length <= 1 ? current : current.filter((partner) => partner.id !== id));
  };

  const addExpenseAgent = () => {
    const id = Number(agentToAdd);
    if (!id || selectedAgentSet.has(id)) return;
    setSelectedAgents((current) => [...current, { id, settled: false }]);
    setAgentToAdd("");
  };

  const removeExpenseAgent = (id) => {
    setSelectedAgents((current) => current.filter((agent) => Number(agent.id) !== Number(id)));
  };

  const toggleExpenseAgentSettled = (id) => {
    setSelectedAgents((current) => current.map((agent) => (
      Number(agent.id) === Number(id) ? { ...agent, settled: !agent.settled } : agent
    )));
  };

  const saveEntry = async () => {
    if ((form.entry_type !== "transfer" && !form.title.trim()) || !form.amount || Number(form.amount) <= 0) {
      alert("Enter title and valid amount");
      return;
    }
    try {
      const payload = {
        ...form,
        title: form.entry_type === "transfer" && !form.title.trim() ? "Bank Transfer" : form.title,
        amount: Number(form.amount),
        bank_id: form.bank_id || defaultExpenseBank?.id || null,
        to_bank_id: form.to_bank_id || null,
      };
      if (form.id) {
        await API.put(`/admin/expenses/${form.id}`, payload);
      } else {
        await API.post("/admin/expenses", payload);
      }
      setForm({ ...emptyForm, bank_id: defaultExpenseBank?.id ? String(defaultExpenseBank.id) : "" });
      await loadExpenses();
      alert(form.id ? "Expense entry updated" : "Expense entry saved");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save entry");
    }
  };

  const editEntry = (entry) => {
    if (entry.source_type === "partner_payment") {
      alert("Partner payment entries are edited from the partner payment popup.");
      return;
    }
    setForm({
      id: entry.id || "",
      entry_date: toDateInputValue(entry.entry_date),
      entry_type: entry.entry_type || "debit",
      title: entry.title || "",
      category: entry.category || "",
      amount: String(Number(entry.amount || 0)),
      note: entry.note || "",
      bank_id: entry.bank_id ? String(entry.bank_id) : "",
      to_bank_id: entry.to_bank_id ? String(entry.to_bank_id) : "",
    });
    setTimeout(() => document.getElementById("expense-entry-form")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const cancelEntryEdit = () => {
    setForm({ ...emptyForm, bank_id: defaultExpenseBank?.id ? String(defaultExpenseBank.id) : "" });
  };

  const saveBank = async () => {
    if (!bankForm.bank_name.trim()) {
      alert("Enter bank name");
      return;
    }
    try {
      await API.post("/admin/expenses/banks", {
        ...bankForm,
        opening_balance: Number(bankForm.opening_balance || 0),
      });
      setBankForm(emptyBankForm);
      await loadExpenses();
      alert("Bank saved");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save bank");
    }
  };

  const editBank = (bank) => {
    setBankForm({
      id: bank.id,
      bank_name: bank.bank_name || "",
      account_label: bank.account_label || "",
      opening_balance: String(Number(bank.opening_balance || 0)),
      is_default_deposit: Boolean(Number(bank.is_default_deposit || 0)),
      is_default_withdraw: Boolean(Number(bank.is_default_withdraw || 0)),
      is_expense_default: Boolean(Number(bank.is_expense_default || 0)),
    });
  };

  const deleteBank = async (bank) => {
    if (!window.confirm(`Hide bank "${bank.bank_name}"? Existing ledger entries stay saved.`)) return;
    try {
      await API.delete(`/admin/expenses/banks/${bank.id}`);
      await loadExpenses();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to delete bank");
    }
  };

  const saveSettings = async () => {
    if (Math.round(splitTotal * 100) / 100 !== 100) {
      alert("Split percentage must total 100");
      return;
    }
    try {
      await API.post("/admin/expenses/settings", {
        partners: partners.map((partner) => ({
          id: partner.id,
          name: partner.name,
          percent: Number(partner.percent || 0),
        })),
      });
      setData((current) => ({
        ...current,
        settings: {
          ...(current.settings || {}),
          partners,
        },
        summary: {
          ...(current.summary || {}),
          partner_shares: previewPartnerShares,
        },
      }));
      await loadExpenses();
      alert("Business partner split saved");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save split settings");
    }
  };

  const saveAgents = async () => {
    try {
      await API.post("/admin/expenses/agents", {
        agents: selectedAgents.map((agent) => ({
          id: Number(agent.id),
          settled: Boolean(agent.settled),
        })),
      });
      await loadExpenses();
      alert("Agent commission settings saved");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save agent settings");
    }
  };

  const openPartnerDetails = (partner) => {
    const partnerId = String(partner.id || partner.name);
    setOpenPartner({
      ...partner,
      settlement: partnerSettlementMap.get(partnerId) || {},
    });
    setPartnerPaymentForm(emptyPartnerPaymentForm);
  };

  const savePartnerPayment = async () => {
    if (!openPartner) return;
    if (!partnerPaymentForm.amount || Number(partnerPaymentForm.amount) <= 0) {
      alert("Enter valid payment amount");
      return;
    }
    try {
      await API.post("/admin/expenses/partner-payments", {
        partner_id: String(openPartner.id || openPartner.name),
        partner_name: openPartner.name,
        ...partnerPaymentForm,
        amount: Number(partnerPaymentForm.amount),
        bank_id: partnerPaymentForm.bank_id || defaultExpenseBank?.id || null,
      });
      await loadExpenses();
      setPartnerPaymentForm(emptyPartnerPaymentForm);
      setOpenPartner(null);
      alert("Partner payment saved");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save partner payment");
    }
  };

  const deletePartnerPayment = async (payment) => {
    if (!window.confirm("Delete this partner payment entry?")) return;
    try {
      await API.delete(`/admin/expenses/partner-payments/${payment.id}`);
      await loadExpenses();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to delete partner payment");
    }
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm(`Delete entry "${entry.title}" from active ledger?`)) return;
    try {
      if (entry.source_type === "partner_payment" && entry.source_id) {
        await API.delete(`/admin/expenses/partner-payments/${entry.source_id}`);
      } else {
        await API.delete(`/admin/expenses/${entry.id}`);
      }
      await loadExpenses();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to delete entry");
    }
  };

  const restoreEntry = async (entry) => {
    try {
      await API.post(`/admin/expenses/${entry.id}/restore`);
      await loadExpenses();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to restore entry");
    }
  };

  return (
    <AdminLayout>
      <section style={page}>
        <header style={header}>
          <div>
            <h1 style={title}>Expenses</h1>
            <p style={subtitle}>Website profit, manual entries, closing bank balance, and deleted ledger history.</p>
          </div>
          <button type="button" style={refreshButton} onClick={loadExpenses}>
            <RotateCcw size={16} />
            Refresh
          </button>
        </header>

        <section style={summaryGrid}>
          <Metric label="Website Profit" value={formatMoney(data.summary.totalProfit)} color="#16a34a" />
          <Metric label="Settled Agent Minus" value={formatMoney(data.summary.totalSettledAgentCommission)} color="#f97316" />
          <Metric label="Pending Agent Commission" value={formatMoney(data.summary.totalPendingAgentCommission)} color="#d97706" />
          <Metric label="Profit After Agents" value={formatMoney(data.summary.profitAfterAgentCommission)} color="#0891b2" />
          <Metric label="Manual Credit" value={formatMoney(data.summary.totalCredit)} color="#2563eb" />
          <Metric label="Manual Debit" value={formatMoney(data.summary.totalDebit)} color="#dc2626" />
          <Metric label="Closing Bank Balance" value={formatMoney(data.summary.closingBankBalance)} color="#7c3aed" featured />
          <Metric label="Bank Ledger Total" value={formatMoney(data.summary.bankClosingBalance)} color="#0f172a" />
        </section>

        <section style={card}>
          <div style={cardHeader}>
            <div>
              <h3 style={cardTitle}>Banks</h3>
              <p style={miniText}>Add bank opening balances, set default deposit/withdraw/expense bank, and track transfer movement.</p>
            </div>
            <span style={pill}>{banks.length} banks</span>
          </div>
          <div style={bankGrid}>
            {banks.map((bank) => (
              <article key={bank.id} style={bankCard}>
                <div>
                  <strong style={entryTitle}>{bank.bank_name}</strong>
                  <div style={entryMeta}>{bank.account_label || "Bank account"}</div>
                </div>
                <strong style={bankBalance}>{formatMoney(bank.current_balance)}</strong>
                <div style={bankTags}>
                  {Number(bank.is_default_deposit || 0) ? <span style={bankTag}>Deposit</span> : null}
                  {Number(bank.is_default_withdraw || 0) ? <span style={bankTag}>Withdraw</span> : null}
                  {Number(bank.is_expense_default || 0) ? <span style={bankTag}>Expense</span> : null}
                </div>
                <div style={bankActions}>
                  <button type="button" style={smallEditButton} onClick={() => editBank(bank)}>Edit</button>
                  <button type="button" style={smallDeleteButton} onClick={() => deleteBank(bank)}>Hide</button>
                </div>
              </article>
            ))}
            {!banks.length ? <div style={emptyAgentBox}>No banks added yet. Add your first bank below.</div> : null}
          </div>
          <div style={isMobile ? mobileFormGrid : bankFormGrid}>
            <Field label="Bank Name">
              <input style={input} value={bankForm.bank_name} onChange={(event) => setBankForm((current) => ({ ...current, bank_name: event.target.value }))} placeholder="HDFC, SBI, Cash..." />
            </Field>
            <Field label="Account Label">
              <input style={input} value={bankForm.account_label} onChange={(event) => setBankForm((current) => ({ ...current, account_label: event.target.value }))} placeholder="Main, UPI, withdrawal..." />
            </Field>
            <Field label="Opening Balance">
              <input style={input} value={bankForm.opening_balance} onChange={(event) => setBankForm((current) => ({ ...current, opening_balance: event.target.value.replace(/[^\d.]/g, "") }))} inputMode="decimal" placeholder="0" />
            </Field>
            <div style={bankCheckRow}>
              <label style={checkLabel}><input type="checkbox" checked={bankForm.is_default_deposit} onChange={(event) => setBankForm((current) => ({ ...current, is_default_deposit: event.target.checked }))} /> Deposit</label>
              <label style={checkLabel}><input type="checkbox" checked={bankForm.is_default_withdraw} onChange={(event) => setBankForm((current) => ({ ...current, is_default_withdraw: event.target.checked }))} /> Withdraw</label>
              <label style={checkLabel}><input type="checkbox" checked={bankForm.is_expense_default} onChange={(event) => setBankForm((current) => ({ ...current, is_expense_default: event.target.checked }))} /> Expense</label>
            </div>
          </div>
          <div style={bankActions}>
            <button type="button" style={primaryButton} onClick={saveBank}><Save size={16} /> {bankForm.id ? "Update Bank" : "Add Bank"}</button>
            {bankForm.id ? <button type="button" style={secondaryButton} onClick={() => setBankForm(emptyBankForm)}>Cancel Edit</button> : null}
          </div>
        </section>

        <section style={card}>
          <div style={cardHeader}>
            <div>
              <h3 style={cardTitle}>Agent Commission</h3>
              <p style={miniText}>Agent user profit = deposit minus withdraw under that agent. Commission is shown here; only Settled rows are deducted before partner split.</p>
            </div>
            <span style={agentTotalPill}>Settled: {formatMoney(data.summary.totalSettledAgentCommission)}</span>
          </div>

          <div style={isMobile ? mobileAgentControls : agentControls}>
            <select style={input} value={agentToAdd} onChange={(event) => setAgentToAdd(event.target.value)}>
              <option value="">Select agent</option>
              {addableAgents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name || agent.email || `Agent #${agent.id}`} - {Number(agent.commission_rate || 0)}%
                </option>
              ))}
            </select>
            <button type="button" style={agentAddButton} onClick={addExpenseAgent} disabled={!agentToAdd}>
              <Users size={16} />
              Add Agent
            </button>
            <button type="button" style={secondaryButton} onClick={saveAgents}>
              Save Agents
            </button>
          </div>

          <div style={agentGrid}>
            {visibleAgentCommissions.map((agent) => (
              <article key={agent.id} style={agentCard}>
                <div style={agentCardTop}>
                  <div>
                    <strong style={agentName}>{agent.name || agent.email || `Agent #${agent.id}`}</strong>
                    <span style={agentRate}>
                      {Number(agent.commission_rate || 0)}% commission · total {formatMoney(agent.exact_commission ?? agent.commission_amount)} · pending {formatMoney(agent.pending_commission)}
                    </span>
                  </div>
                  <div style={agentActions}>
                    <button
                      type="button"
                      style={agent.settled ? settledButton : pendingButton}
                      onClick={() => toggleExpenseAgentSettled(agent.id)}
                    >
                      {agent.settled ? "Settled" : "Pending"}
                    </button>
                    <button type="button" style={agentRemoveButton} onClick={() => removeExpenseAgent(agent.id)}>Remove</button>
                  </div>
                </div>
                <div style={agentStatGrid}>
                  <MiniStat label="Deposit" value={formatMoney(agent.total_deposit)} color="#16a34a" />
                  <MiniStat label="Withdraw" value={formatMoney(agent.total_withdraw)} color="#dc2626" />
                  <MiniStat label="Agent User Profit" value={formatMoney(agent.net_profit)} color="#0f172a" />
                  <MiniStat label="Total Commission" value={formatMoney(agent.exact_commission ?? agent.commission_amount)} color="#7c3aed" />
                  <MiniStat label="Already Settled" value={formatMoney(agent.settled_amount)} color="#16a34a" />
                  <MiniStat label="Pending/New" value={formatMoney(agent.pending_commission)} color="#d97706" />
                  <MiniStat label="Minus From Site" value={formatMoney(agent.deducted_commission)} color="#f97316" />
                </div>
              </article>
            ))}
            {!selectedAgents.length && (
              <div style={emptyAgentBox}>No agent commission selected yet. Add an agent above, save, then mark Settled only when it should reduce site profit.</div>
            )}
            {selectedAgents.length > 0 && !visibleAgentCommissions.length && (
              <div style={emptyAgentBox}>Save or refresh to calculate selected agent commission.</div>
            )}
            {!availableAgents.length && (
              <div style={emptyAgentBox}>No agents found. Create agents in Agent Program first, then come back here.</div>
            )}
          </div>
        </section>

        <section style={isMobile ? mobileSplitGrid : splitGrid}>
          {previewPartnerShares.map((partner) => (
            <article key={partner.id || partner.name} style={splitCard}>
              <div>
                <span style={splitLabel}>{partner.name}</span>
                <strong style={splitValue}>{formatMoney(partner.amount)}</strong>
                <button type="button" style={partnerEyeButton} onClick={() => openPartnerDetails(partner)}>
                  <Eye size={14} />
                  Payments
                </button>
              </div>
              <div style={partnerMiniSummary}>
                <small style={splitMeta}>{Number(partner.percent || 0)}% share</small>
                <small style={partnerRemainingText}>
                  Balance {formatMoney(partnerSettlementMap.get(String(partner.id || partner.name))?.remaining_amount ?? partner.amount)}
                </small>
              </div>
            </article>
          ))}
        </section>

        <div style={isMobile ? mobileMainGrid : mainGrid}>
          <section id="expense-entry-form" style={card}>
            <div style={cardHeader}>
              <h3 style={cardTitle}>{form.id ? "Edit Entry" : "New Entry"}</h3>
              <span style={pill}>{form.id ? `#${form.id}` : form.entry_type === "credit" ? "Add" : "Less"}</span>
            </div>
            <div style={isMobile ? mobileFormGrid : formGrid}>
              <Field label="Date">
                <input style={input} type="date" value={form.entry_date} onChange={(event) => updateForm("entry_date", event.target.value)} />
              </Field>
              <Field label="Type">
                <select style={input} value={form.entry_type} onChange={(event) => updateForm("entry_type", event.target.value)}>
                  <option value="debit">Debit / Expense</option>
                  <option value="credit">Credit / Add</option>
                  <option value="transfer">Bank To Bank Transfer</option>
                </select>
              </Field>
              <Field label="Title">
                <input style={input} value={form.title} onChange={(event) => updateForm("title", event.target.value)} placeholder="Rent, salary, ad spend..." />
              </Field>
              <Field label="Category">
                <input style={input} value={form.category} onChange={(event) => updateForm("category", event.target.value)} placeholder="Office, ads, bank..." />
              </Field>
              <Field label="Amount">
                <input style={input} value={form.amount} onChange={(event) => updateForm("amount", event.target.value.replace(/[^\d.]/g, ""))} inputMode="decimal" placeholder="0" />
              </Field>
              <Field label={form.entry_type === "transfer" ? "From Bank" : "Bank"}>
                <select style={input} value={form.bank_id || defaultExpenseBank?.id || ""} onChange={(event) => updateForm("bank_id", event.target.value)}>
                  <option value="">No bank selected</option>
                  {banks.map((bank) => <option key={bank.id} value={bank.id}>{bank.bank_name}</option>)}
                </select>
              </Field>
              {form.entry_type === "transfer" ? (
                <Field label="To Bank">
                  <select style={input} value={form.to_bank_id} onChange={(event) => updateForm("to_bank_id", event.target.value)}>
                    <option value="">Select target bank</option>
                    {banks.map((bank) => <option key={bank.id} value={bank.id}>{bank.bank_name}</option>)}
                  </select>
                </Field>
              ) : null}
              <Field label="Note">
                <input style={input} value={form.note} onChange={(event) => updateForm("note", event.target.value)} placeholder="Optional remark" />
              </Field>
            </div>
            <div style={entryFormActions}>
              <button type="button" style={primaryButton} onClick={saveEntry}>
                <Save size={16} />
                {form.id ? "Update Entry" : "Save Entry"}
              </button>
              {form.id ? (
                <button type="button" style={secondaryButton} onClick={cancelEntryEdit}>
                  Cancel Edit
                </button>
              ) : null}
            </div>
          </section>

          <section style={card}>
            <div style={cardHeader}>
              <div>
                <h3 style={cardTitle}>Business Partners</h3>
                <p style={miniText}>Split base: Profit After Agents {formatMoney(splitBase)}. Manual entries only affect closing bank balance.</p>
              </div>
              <span style={splitTotal === 100 ? okPill : warnPill}>{splitTotal}%</span>
            </div>
            <div style={partnerList}>
              {partners.map((partner, index) => (
                <div key={partner.id} style={partnerRow}>
                  <input
                    style={partnerNameInput}
                    value={partner.name}
                    onChange={(event) => updatePartner(partner.id, "name", event.target.value)}
                    placeholder={`Partner ${index + 1}`}
                  />
                  <input
                    style={partnerPercentInput}
                    value={partner.percent}
                    onChange={(event) => updatePartner(partner.id, "percent", event.target.value.replace(/[^\d.]/g, ""))}
                    inputMode="decimal"
                    placeholder="%"
                  />
                  <button type="button" style={removePartnerButton} onClick={() => removePartner(partner.id)} disabled={partners.length <= 1}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button type="button" style={addPartnerButton} onClick={addPartner}>Add Partner</button>
            <button type="button" style={secondaryButton} onClick={saveSettings}>
              Save Business Partners
            </button>
          </section>
        </div>

        <section style={card}>
          <div style={ledgerHeader}>
            <div style={tabs}>
              <button type="button" style={tab === "active" ? activeTab : tabButton} onClick={() => setTab("active")}>Active Ledger</button>
              <button type="button" style={tab === "deleted" ? activeTab : tabButton} onClick={() => setTab("deleted")}>Deleted History</button>
            </div>
            <span style={ledgerCount}>{loading ? "Loading..." : `${rows.length} entries`}</span>
          </div>

          <div style={tableWrap}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Date</th>
                  <th style={th}>Entry</th>
                  <th style={th}>Type</th>
                  <th style={th}>Bank</th>
                  <th style={th}>Amount</th>
                  <th style={th}>Created</th>
                  {tab === "deleted" ? <th style={th}>Deleted</th> : null}
                  <th style={th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((entry) => (
                  <tr key={entry.id}>
                    <td style={td}>{formatDate(entry.entry_date)}</td>
                    <td style={td}>
                      <strong style={entryTitle}>{entry.title}</strong>
                      <div style={entryMeta}>{entry.category || "General"}{entry.note ? ` - ${entry.note}` : ""}</div>
                    </td>
                    <td style={td}><span style={typePill(entry.entry_type)}>{entry.entry_type}</span></td>
                    <td style={td}>{formatBankMove(entry)}</td>
                    <td style={{ ...td, color: entry.entry_type === "credit" ? "#16a34a" : "#dc2626", fontWeight: 900 }}>
                      {entry.entry_type === "transfer" ? "" : entry.entry_type === "credit" ? "+" : "-"}{formatMoney(entry.amount)}
                    </td>
                    <td style={td}>
                      <strong style={stampName}>{entry.created_by_name || "Admin"}</strong>
                      <div style={entryMeta}>{formatDateTime(entry.created_at)}</div>
                    </td>
                    {tab === "deleted" ? (
                      <td style={td}>
                        <strong style={stampName}>{entry.deleted_by_name || "Admin"}</strong>
                        <div style={entryMeta}>{formatDateTime(entry.deleted_at)}</div>
                      </td>
                    ) : null}
                    <td style={td}>
                      {tab === "deleted" ? (
                        <button type="button" style={restoreButton} onClick={() => restoreEntry(entry)}>Restore</button>
                      ) : (
                        <div style={entryActionRow}>
                          {entry.source_type !== "partner_payment" ? (
                            <button type="button" style={editEntryButton} onClick={() => editEntry(entry)}><Pencil size={14} /> Edit</button>
                          ) : null}
                          <button type="button" style={deleteButton} onClick={() => deleteEntry(entry)}><Trash2 size={14} /> Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={tab === "deleted" ? 8 : 7} style={emptyCell}>No entries found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {openPartner && (
          <div style={partnerOverlay}>
            <section style={partnerModal}>
              <div style={cardHeader}>
                <div>
                  <h3 style={cardTitle}>{openPartner.name} Payments</h3>
                  <p style={miniText}>Track amount taken from profit share, paid for site expense, or paid back to site.</p>
                </div>
                <button type="button" style={closePartnerButton} onClick={() => setOpenPartner(null)}>x</button>
              </div>

              <div style={partnerSummaryGrid}>
                <MiniStat label="Share" value={formatMoney(openPartner.settlement?.share_amount ?? openPartner.amount)} color="#7c3aed" />
                <MiniStat label="Taken" value={formatMoney(openPartner.settlement?.taken_from_profit)} color="#dc2626" />
                <MiniStat label="Paid Expense" value={formatMoney(openPartner.settlement?.paid_site_expense)} color="#16a34a" />
                <MiniStat label="Balance" value={formatMoney(openPartner.settlement?.remaining_amount ?? openPartner.amount)} color="#0f172a" />
              </div>

              <div style={isMobile ? mobileFormGrid : formGrid}>
                <Field label="Date">
                  <input style={input} type="date" value={partnerPaymentForm.payment_date} onChange={(event) => setPartnerPaymentForm((current) => ({ ...current, payment_date: event.target.value }))} />
                </Field>
                <Field label="Payment Type">
                  <select style={input} value={partnerPaymentForm.payment_type} onChange={(event) => setPartnerPaymentForm((current) => ({ ...current, payment_type: event.target.value }))}>
                    <option value="taken_from_profit">Taken From Profit Share</option>
                    <option value="paid_site_expense">Paid Site Expense</option>
                    <option value="paid_to_site">Paid Back To Site</option>
                  </select>
                </Field>
                <Field label="Amount">
                  <input style={input} value={partnerPaymentForm.amount} onChange={(event) => setPartnerPaymentForm((current) => ({ ...current, amount: event.target.value.replace(/[^\d.]/g, "") }))} inputMode="decimal" placeholder="0" />
                </Field>
                <Field label="Bank">
                  <select style={input} value={partnerPaymentForm.bank_id || defaultExpenseBank?.id || ""} onChange={(event) => setPartnerPaymentForm((current) => ({ ...current, bank_id: event.target.value }))}>
                    <option value="">No bank selected</option>
                    {banks.map((bank) => <option key={bank.id} value={bank.id}>{bank.bank_name}</option>)}
                  </select>
                </Field>
                <Field label="Note">
                  <input style={input} value={partnerPaymentForm.note} onChange={(event) => setPartnerPaymentForm((current) => ({ ...current, note: event.target.value }))} placeholder="Optional note" />
                </Field>
              </div>
              <button type="button" style={primaryButton} onClick={savePartnerPayment}>Save Partner Payment</button>

              <div style={tableWrap}>
                <table style={{ ...table, minWidth: 640 }}>
                  <thead>
                    <tr>
                      <th style={th}>Date</th>
                      <th style={th}>Type</th>
                      <th style={th}>Amount</th>
                      <th style={th}>Bank</th>
                      <th style={th}>By</th>
                      <th style={th}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.partner_payments || [])
                      .filter((payment) => String(payment.partner_id) === String(openPartner.id || openPartner.name))
                      .map((payment) => (
                        <tr key={payment.id}>
                          <td style={td}>{formatDate(payment.payment_date)}</td>
                          <td style={td}><span style={partnerPaymentTypePill(payment.payment_type)}>{formatPartnerPaymentType(payment.payment_type)}</span><div style={entryMeta}>{payment.note || ""}</div></td>
                          <td style={{ ...td, fontWeight: 900 }}>{formatMoney(payment.amount)}</td>
                          <td style={td}>{payment.bank_name || "-"}</td>
                          <td style={td}>{payment.created_by_name || "Admin"}<div style={entryMeta}>{formatDateTime(payment.created_at)}</div></td>
                          <td style={td}><button type="button" style={deleteButton} onClick={() => deletePartnerPayment(payment)}><Trash2 size={14} /> Delete</button></td>
                        </tr>
                      ))}
                    {!(data.partner_payments || []).filter((payment) => String(payment.partner_id) === String(openPartner.id || openPartner.name)).length && (
                      <tr><td colSpan={6} style={emptyCell}>No partner payment entries yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </section>
    </AdminLayout>
  );
}

function Field({ label, children }) {
  return (
    <label style={field}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function Metric({ label, value, color, featured = false }) {
  return (
    <article style={featured ? featuredMetric : metric}>
      <WalletCards size={20} color={color} />
      <span style={metricLabel}>{label}</span>
      <strong style={{ ...metricValue, color }}>{value}</strong>
    </article>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div style={miniStat}>
      <span style={miniStatLabel}>{label}</span>
      <strong style={{ ...miniStatValue, color }}>{value}</strong>
    </div>
  );
}

function formatMoney(value) {
  return `Rs ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function normalizePartners(value) {
  const source = Array.isArray(value) && value.length ? value : defaultPartners;
  return source.map((partner, index) => ({
    id: String(partner?.id || `partner_${index + 1}`),
    name: String(partner?.name || `Partner ${index + 1}`),
    percent: String(partner?.percent ?? 0),
  }));
}

function normalizeAgentSettings(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  return value
    .map((item) => {
      if (typeof item === "object" && item !== null) {
        return { id: Number(item.id), settled: Boolean(item.settled) };
      }
      return { id: Number(item), settled: false };
    })
    .filter((agent) => {
      if (!Number.isInteger(agent.id) || agent.id <= 0 || seen.has(agent.id)) return false;
      seen.add(agent.id);
      return true;
    });
}

function formatPartnerPaymentType(value) {
  const type = String(value || "");
  if (type === "paid_site_expense") return "Paid Site Expense";
  if (type === "paid_to_site") return "Paid Back To Site";
  return "Taken From Profit";
}

function formatBankMove(entry = {}) {
  if (String(entry.entry_type || "").toLowerCase() === "transfer") {
    return `${entry.bank_name || "-"} -> ${entry.to_bank_name || "-"}`;
  }
  return entry.bank_name || "-";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function toDateInputValue(value) {
  if (!value) return getIndiaDateValue();
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return getIndiaDateValue();
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function getIndiaDateValue() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

const page = { display: "grid", gap: 16 };
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" };
const title = { margin: 0, fontSize: 24, fontWeight: 900, color: "#0f172a" };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: 13 };
const refreshButton = { height: 44, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", borderRadius: 8, padding: "0 14px", display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 900, cursor: "pointer" };
const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 };
const metric = { minHeight: 104, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, display: "grid", gap: 7, alignContent: "center", boxShadow: "0 10px 24px rgba(15,23,42,.04)" };
const featuredMetric = { ...metric, background: "linear-gradient(135deg,#fff7ed,#ffffff)", border: "1px solid #fed7aa" };
const metricLabel = { color: "#64748b", fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".05em" };
const metricValue = { fontSize: 20, fontWeight: 900 };
const splitGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 };
const mobileSplitGrid = { display: "grid", gridTemplateColumns: "1fr", gap: 10 };
const splitCard = { minHeight: 78, border: "1px solid #e2e8f0", background: "linear-gradient(180deg,#ffffff,#f8fafc)", borderRadius: 8, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 };
const splitLabel = { display: "block", color: "#475569", fontSize: 12, fontWeight: 900, textTransform: "uppercase" };
const splitValue = { display: "block", marginTop: 4, color: "#0f172a", fontSize: 18, fontWeight: 900 };
const splitMeta = { color: "#2563eb", fontSize: 12, fontWeight: 900 };
const partnerEyeButton = { marginTop: 8, minHeight: 30, border: "1px solid #bfdbfe", borderRadius: 999, background: "#eff6ff", color: "#2563eb", padding: "0 10px", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 900, cursor: "pointer" };
const partnerMiniSummary = { display: "grid", gap: 4, textAlign: "right" };
const partnerRemainingText = { color: "#16a34a", fontSize: 12, fontWeight: 900 };
const mainGrid = { display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(320px, .8fr)", gap: 12 };
const mobileMainGrid = { display: "grid", gridTemplateColumns: "1fr", gap: 12 };
const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, display: "grid", gap: 14, boxShadow: "0 10px 24px rgba(15,23,42,.04)", overflow: "hidden" };
const cardHeader = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 };
const cardTitle = { margin: 0, fontSize: 17, fontWeight: 900, color: "#0f172a" };
const miniText = { margin: "4px 0 0", color: "#64748b", fontSize: 12, lineHeight: 1.45 };
const pill = { padding: "6px 10px", borderRadius: 999, background: "#f1f5f9", color: "#334155", fontSize: 12, fontWeight: 900 };
const okPill = { ...pill, background: "#dcfce7", color: "#15803d" };
const warnPill = { ...pill, background: "#fee2e2", color: "#b91c1c" };
const formGrid = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 };
const mobileFormGrid = { display: "grid", gridTemplateColumns: "1fr", gap: 12 };
const bankFormGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, alignItems: "end" };
const bankGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 };
const bankCard = { border: "1px solid #dbeafe", background: "linear-gradient(135deg,#eff6ff,#ffffff)", borderRadius: 10, padding: 12, display: "grid", gap: 8 };
const bankBalance = { color: "#0f172a", fontSize: 18, fontWeight: 950 };
const bankTags = { display: "flex", gap: 6, flexWrap: "wrap", minHeight: 24 };
const bankTag = { display: "inline-flex", alignItems: "center", height: 22, padding: "0 8px", borderRadius: 999, background: "#dcfce7", color: "#15803d", fontSize: 11, fontWeight: 900 };
const bankActions = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };
const smallEditButton = { minHeight: 32, border: "1px solid #bfdbfe", borderRadius: 8, background: "#fff", color: "#2563eb", padding: "0 10px", fontWeight: 900, cursor: "pointer" };
const smallDeleteButton = { minHeight: 32, border: 0, borderRadius: 8, background: "#fee2e2", color: "#b91c1c", padding: "0 10px", fontWeight: 900, cursor: "pointer" };
const bankCheckRow = { minHeight: 44, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" };
const checkLabel = { display: "inline-flex", alignItems: "center", gap: 5, color: "#334155", fontSize: 12, fontWeight: 900 };
const partnerList = { display: "grid", gap: 10 };
const partnerRow = { display: "grid", gridTemplateColumns: "minmax(0, 1fr) 86px auto", gap: 8, alignItems: "center" };
const partnerNameInput = { height: 44, width: "100%", border: "1px solid #cbd5e1", borderRadius: 8, padding: "0 12px", fontSize: 14, boxSizing: "border-box", background: "#fff", minWidth: 0 };
const partnerPercentInput = { ...partnerNameInput, textAlign: "center", fontWeight: 900 };
const removePartnerButton = { height: 40, border: 0, borderRadius: 8, background: "#fee2e2", color: "#b91c1c", padding: "0 10px", fontWeight: 900, cursor: "pointer" };
const addPartnerButton = { height: 42, border: "1px dashed #93c5fd", borderRadius: 8, background: "#eff6ff", color: "#1d4ed8", fontWeight: 900, cursor: "pointer" };
const field = { display: "grid", gap: 6, color: "#334155", fontSize: 12, fontWeight: 900 };
const input = { height: 44, width: "100%", border: "1px solid #cbd5e1", borderRadius: 8, padding: "0 12px", fontSize: 14, boxSizing: "border-box", background: "#fff" };
const agentTotalPill = { ...pill, background: "#fff7ed", color: "#ea580c", border: "1px solid #fed7aa", whiteSpace: "nowrap" };
const agentControls = { display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto", gap: 10, alignItems: "center" };
const mobileAgentControls = { display: "grid", gridTemplateColumns: "1fr", gap: 10 };
const agentAddButton = { height: 44, border: 0, borderRadius: 8, background: "linear-gradient(135deg,#f97316,#ea580c)", color: "#fff", fontWeight: 900, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer" };
const agentGrid = { display: "grid", gap: 10 };
const agentCard = { border: "1px solid #fed7aa", background: "linear-gradient(135deg,#fff7ed,#ffffff)", borderRadius: 10, padding: 12, display: "grid", gap: 12 };
const agentCardTop = { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", flexWrap: "wrap" };
const agentName = { display: "block", color: "#0f172a", fontSize: 15, fontWeight: 900 };
const agentRate = { display: "block", marginTop: 3, color: "#ea580c", fontSize: 12, fontWeight: 900 };
const agentActions = { display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" };
const pendingButton = { minHeight: 32, border: 0, borderRadius: 8, background: "#fef3c7", color: "#92400e", padding: "0 10px", fontWeight: 900, cursor: "pointer" };
const settledButton = { ...pendingButton, background: "#dcfce7", color: "#15803d" };
const agentRemoveButton = { minHeight: 32, border: 0, borderRadius: 8, background: "#fee2e2", color: "#b91c1c", padding: "0 10px", fontWeight: 900, cursor: "pointer" };
const agentStatGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(135px, 1fr))", gap: 8 };
const miniStat = { border: "1px solid #e2e8f0", background: "rgba(255,255,255,.82)", borderRadius: 8, padding: "10px 11px", display: "grid", gap: 3 };
const miniStatLabel = { color: "#64748b", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".04em" };
const miniStatValue = { color: "#0f172a", fontSize: 14, fontWeight: 900 };
const emptyAgentBox = { border: "1px dashed #cbd5e1", background: "#f8fafc", color: "#64748b", borderRadius: 8, padding: 14, fontSize: 13, fontWeight: 800, textAlign: "center" };
const primaryButton = { height: 46, border: 0, borderRadius: 8, background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff", fontWeight: 900, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer" };
const secondaryButton = { height: 46, border: "1px solid #bfdbfe", borderRadius: 8, background: "#eff6ff", color: "#2563eb", fontWeight: 900, cursor: "pointer" };
const entryFormActions = { display: "flex", gap: 10, flexWrap: "wrap" };
const ledgerHeader = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" };
const tabs = { display: "inline-flex", background: "#f1f5f9", padding: 4, borderRadius: 8, gap: 4 };
const tabButton = { height: 38, border: 0, background: "transparent", borderRadius: 6, padding: "0 14px", color: "#475569", fontWeight: 900, cursor: "pointer" };
const activeTab = { ...tabButton, background: "#fff", color: "#2563eb", boxShadow: "0 4px 12px rgba(15,23,42,.08)" };
const ledgerCount = { color: "#64748b", fontSize: 12, fontWeight: 900 };
const partnerOverlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.42)", zIndex: 2000, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "28px 12px", overflowY: "auto" };
const partnerModal = { width: "min(920px, 100%)", background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", boxShadow: "0 24px 70px rgba(15,23,42,.24)", padding: 16, display: "grid", gap: 14 };
const closePartnerButton = { width: 36, height: 36, border: 0, borderRadius: 999, background: "#fee2e2", color: "#b91c1c", fontWeight: 900, cursor: "pointer" };
const partnerSummaryGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 };
const tableWrap = { width: "100%", overflowX: "auto" };
const table = { width: "100%", borderCollapse: "collapse", minWidth: 900 };
const th = { textAlign: "left", padding: "12px 10px", color: "#64748b", fontSize: 11, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" };
const td = { padding: "12px 10px", borderBottom: "1px solid #f1f5f9", color: "#0f172a", fontSize: 13, verticalAlign: "top" };
const entryTitle = { display: "block", fontWeight: 900 };
const entryMeta = { marginTop: 3, color: "#64748b", fontSize: 12 };
const stampName = { fontWeight: 900, color: "#334155" };
const typePill = (type) => {
  const clean = String(type || "").toLowerCase();
  if (clean === "transfer") return { display: "inline-flex", padding: "6px 10px", borderRadius: 999, background: "#dbeafe", color: "#1d4ed8", fontWeight: 900, textTransform: "capitalize" };
  return { display: "inline-flex", padding: "6px 10px", borderRadius: 999, background: clean === "credit" ? "#dcfce7" : "#fee2e2", color: clean === "credit" ? "#15803d" : "#b91c1c", fontWeight: 900, textTransform: "capitalize" };
};
const partnerPaymentTypePill = (type) => {
  const normalized = String(type || "");
  if (normalized === "paid_site_expense") return { ...typePill("credit"), background: "#dbeafe", color: "#1d4ed8" };
  if (normalized === "paid_to_site") return { ...typePill("credit"), background: "#dcfce7", color: "#15803d" };
  return typePill("debit");
};
const entryActionRow = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };
const editEntryButton = { minHeight: 34, border: "1px solid #bfdbfe", borderRadius: 8, background: "#eff6ff", color: "#2563eb", padding: "0 10px", display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 900, cursor: "pointer" };
const deleteButton = { minHeight: 34, border: 0, borderRadius: 8, background: "#fee2e2", color: "#b91c1c", padding: "0 10px", display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 900, cursor: "pointer" };
const restoreButton = { minHeight: 34, border: 0, borderRadius: 8, background: "#dcfce7", color: "#15803d", padding: "0 12px", fontWeight: 900, cursor: "pointer" };
const emptyCell = { padding: 30, textAlign: "center", color: "#94a3b8", fontWeight: 800 };

export default AdminExpenses;
